// Wholesale & Farm Products page content.
// Channel descriptions are drawn from worldfarmers.org/markets ("many of our
// farmers choose to aggregate their produce and sell wholesale to grocery stores,
// food hubs, mobile markets and their churches"). Seasonal lineups mirror the
// CSA "shares through the seasons" lists plus cultural crops from the crops
// database. Specific retail locations are intentionally omitted on this page.
// The contact form is MOCKED (no backend) until the wholesale inbox is confirmed.

export const WHOLESALE_HERO = {
  eyebrow: "Wholesale & farm products",
  title: "Source culturally relevant crops directly from the farmers who grow them",
  lede:
    "Grocers, food hubs, mobile markets, restaurants, and institutions: partner with World Farmers to bring fresh, hard-to-find crops from the Flats Mentor Farm to your customers.",
  image: "/images/wf/vlai/vlai-3517.jpg",
  imageAlt: "Freshly harvested greens soak in wash tubs at the farm wash station.",
};

export const WHOLESALE_INTRO = {
  eyebrow: "For B2B buyers",
  title: "Aggregated harvests, one point of contact",
  paragraphs: [
    "In addition to farmers\u2019 markets, many of our farmers choose to aggregate their produce and sell wholesale to grocery stores, food hubs, mobile markets, and their churches. World Farmers coordinates crop planning, food-safety practices, and logistics so buyers get consistent volume from a community of skilled growers.",
    "Our farmers produce over 55 acres of ethnic specialty crops on the Flats Mentor Farm in Lancaster, MA \u2014 leafy greens, herbs, fruiting vegetables, and cultural crops that are difficult to find from conventional distributors.",
  ],
  stats: [
    { id: "acres", value: "55+", label: "Acres in production" },
    { id: "crops", value: "80+", label: "Crop varieties grown" },
    { id: "farmers", value: "260+", label: "Farmers in the program" },
  ],
};

export const WHOLESALE_CHANNELS = [
  { id: "grocery", title: "Grocery stores & co-ops", body: "Case-lot leafy greens, herbs, and specialty vegetables for produce departments serving diverse communities." },
  { id: "food-hubs", title: "Food hubs & distributors", body: "Aggregated volume from multiple growers with coordinated harvest and pack schedules." },
  { id: "mobile", title: "Mobile markets & community programs", body: "Affordable, culturally relevant produce for mobile markets, food access programs, and faith communities." },
  { id: "restaurants", title: "Restaurants & institutions", body: "Chef-driven orders of amaranth, jil\u00f3, bitter melon, Thai basil, and more \u2014 harvested to order in season." },
];

export const WHOLESALE_SEASONS = [
  {
    id: "early",
    label: "Early season",
    months: "June \u2013 early July",
    crops: ["Lettuce mix", "Arugula", "Garlic scapes", "Basil", "Summer squash", "Red radish", "Snap peas", "Scallions", "Pea tendrils", "Bok choy"],
  },
  {
    id: "peak",
    label: "Peak season",
    months: "July \u2013 September",
    crops: ["Amaranth", "Jil\u00f3", "Bitter melon", "Okra", "Tomatoes", "Green bell peppers", "Hot Asian peppers", "Cilantro", "Swiss chard", "Collard greens", "Yardlong beans", "Water spinach"],
  },
  {
    id: "fall",
    label: "Fall season",
    months: "September \u2013 October",
    crops: ["Beets", "Parsley", "Kale", "Onions", "Green beans", "Eggplant", "Winter squash", "Carrots", "Daikon radish", "Sweet potato leaves"],
  },
];

export const WHOLESALE_BENEFITS = [
  "Culturally relevant crops your customers can\u2019t find elsewhere",
  "Consistent volume from a community of 260+ skilled growers",
  "Food-safety trained farmers and coordinated wash/pack",
  "Every order directly funds immigrant and refugee farm businesses",
];

export const WHOLESALE_BUYER_TYPES = [
  "Grocery store / co-op",
  "Food hub / distributor",
  "Mobile market / food access program",
  "Restaurant / caterer",
  "School / hospital / institution",
  "Other",
];

export const WHOLESALE_CONTACT = {
  eyebrow: "Contact our Wholesale Team",
  title: "Tell us what you\u2019re looking for",
  body:
    "Share a little about your business and the crops and volumes you need. Our Wholesale Team will follow up to discuss availability, pricing, and delivery.",
  phone: "(978) 706-7935",
  phoneHref: "tel:+19787067935",
};

// Month-by-month availability for the wholesale timeline (typical Flats Mentor
// Farm season; months 1-12). Illustrative until the client confirms a current list.
export const WHOLESALE_SEASONALITY = [
  { crop: "Lettuce mix", months: [5, 6, 7, 8, 9, 10] },
  { crop: "Arugula", months: [5, 6, 7, 8, 9, 10] },
  { crop: "Pea tendrils", months: [5, 6, 7] },
  { crop: "Radish", months: [5, 6, 7, 8, 9, 10] },
  { crop: "Bok choy", months: [5, 6, 7, 8, 9, 10] },
  { crop: "Garlic scapes", months: [6, 7] },
  { crop: "Snap peas", months: [6, 7] },
  { crop: "Scallions", months: [6, 7, 8, 9, 10] },
  { crop: "Basil", months: [6, 7, 8, 9] },
  { crop: "Cilantro", months: [6, 7, 8, 9, 10] },
  { crop: "Summer squash", months: [6, 7, 8, 9] },
  { crop: "Swiss chard", months: [6, 7, 8, 9, 10] },
  { crop: "Collard greens", months: [6, 7, 8, 9, 10, 11] },
  { crop: "Kale", months: [6, 7, 8, 9, 10, 11] },
  { crop: "Amaranth", months: [7, 8, 9] },
  { crop: "Jil\u00f3", months: [7, 8, 9] },
  { crop: "Bitter melon", months: [7, 8, 9] },
  { crop: "Water spinach", months: [7, 8, 9] },
  { crop: "Sweet potato leaves", months: [7, 8, 9] },
  { crop: "Yardlong beans", months: [7, 8, 9] },
  { crop: "Okra", months: [7, 8, 9] },
  { crop: "Tomatoes", months: [7, 8, 9, 10] },
  { crop: "Bell peppers", months: [7, 8, 9, 10] },
  { crop: "Hot Asian peppers", months: [7, 8, 9, 10] },
  { crop: "Eggplant", months: [7, 8, 9, 10] },
  { crop: "Green beans", months: [7, 8, 9] },
  { crop: "Beets", months: [7, 8, 9, 10, 11] },
  { crop: "Carrots", months: [7, 8, 9, 10, 11] },
  { crop: "Onions", months: [8, 9, 10] },
  { crop: "Winter squash", months: [9, 10] },
  { crop: "Daikon radish", months: [9, 10, 11] },
];
